import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Car, Menu, X, Globe, LogOut, ChevronDown, Ship } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { SHIP_COUNTRIES } from '@/data/shipCountries';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const PublicHeader = ({ currency, onCurrencyChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shippedOpen, setShippedOpen] = useState(false);
  const [shippedMobileOpen, setShippedMobileOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/public/company-info`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Vehicles', path: '/vehicles' },
    { label: 'Shipped', path: '/shipped' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'About Us', path: '/about' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isShippedActive = location.pathname === '/shipped' || location.pathname.startsWith('/shipped/');

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-gray-700 shadow-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer flex-shrink-0" 
            onClick={() => navigate('/')}
          >
            <img 
              src="/GTC_Logo.png" 
              alt="Global Trade Car" 
              className="h-14 w-auto"
            />
          </div>

          {/* Desktop Navigation - Main Nav Items */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) =>
              item.path === '/shipped' ? (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setShippedOpen(true)}
                  onMouseLeave={() => setShippedOpen(false)}
                >
                  <button
                    onClick={() => setShippedOpen(!shippedOpen)}
                    onFocus={() => setShippedOpen(true)}
                    className={`text-sm font-medium transition-colors pb-1 whitespace-nowrap inline-flex items-center gap-1 ${
                      isShippedActive
                        ? 'text-red-400 border-b-2 border-red-400'
                        : 'text-gray-300 hover:text-white hover:border-b-2 hover:border-gray-600'
                    }`}
                  >
                    Shipped
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${shippedOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {shippedOpen && (
                    <div className="absolute left-0 top-full w-64 pt-2 z-50">
                      <div className="rounded-lg bg-gray-900 border border-gray-700 shadow-2xl py-2">
                        <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-gray-500">
                          Destinations
                        </div>
                        {SHIP_COUNTRIES.map((c) => (
                          <button
                            key={c.slug}
                            onClick={() => {
                              setShippedOpen(false);
                              navigate(`/shipped/${c.slug}`);
                            }}
                            className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white"
                          >
                            <span className="text-base">{c.emoji}</span>
                            {c.name}
                          </button>
                        ))}
                        <div className="h-px bg-gray-700 my-1.5" />
                        <button
                          onClick={() => {
                            setShippedOpen(false);
                            navigate('/shipped');
                          }}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-cyan-400 hover:bg-gray-800 hover:text-cyan-300"
                        >
                          <Ship className="h-4 w-4" />
                          All Shipped Vehicles
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`text-sm font-medium transition-colors pb-1 whitespace-nowrap ${
                    isActive(item.path)
                      ? 'text-red-400 border-b-2 border-red-400'
                      : 'text-gray-300 hover:text-white hover:border-b-2 hover:border-gray-600'
                  }`}
                >
                  {item.label}
                </button>
              )
            )}
          </nav>

          {/* Right Side - Auth Buttons & Currency */}
          <div className="flex items-center space-x-3">
            {/* Auth Buttons - Desktop Only */}
            <div className="hidden lg:flex items-center space-x-2">
              {isAuthenticated() ? (
                <>
                  <Button
                    onClick={() => navigate('/customer/dashboard')}
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white hover:bg-gray-700"
                  >
                    {customer?.full_name || 'My Account'}
                  </Button>
                  <Button
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/customer/login')}
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white hover:bg-gray-700"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => navigate('/customer/register')}
                    size="sm"
                    className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>

            {/* Currency Display */}
            <div className="flex items-center text-gray-300 px-3 py-1.5 bg-gray-800/50 rounded border border-gray-600">
              <Globe className="h-4 w-4 mr-1 text-gray-300" />
              <span className="text-gray-300 font-medium">USD</span>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-700 bg-gray-900">
          <nav className="px-4 py-4 space-y-2">
            {navItems.map((item) =>
              item.path === '/shipped' ? (
                <div key={item.path}>
                  <button
                    onClick={() => setShippedMobileOpen(!shippedMobileOpen)}
                    className="flex items-center justify-between w-full text-left px-4 py-2 rounded-lg text-base font-medium text-gray-300 hover:bg-gray-800"
                  >
                    <span>Shipped</span>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${shippedMobileOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {shippedMobileOpen && (
                    <div className="ml-4 pl-3 border-l-2 border-gray-700 space-y-1 py-1">
                      {SHIP_COUNTRIES.map((c) => (
                        <button
                          key={c.slug}
                          onClick={() => {
                            setShippedMobileOpen(false);
                            setMobileMenuOpen(false);
                            navigate(`/shipped/${c.slug}`);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-gray-800"
                        >
                          <span className="text-base">{c.emoji}</span>
                          {c.name}
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setShippedMobileOpen(false);
                          setMobileMenuOpen(false);
                          navigate('/shipped');
                        }}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 rounded-lg text-sm text-cyan-400 hover:bg-gray-800"
                      >
                        <Ship className="h-4 w-4" />
                        All Shipped Vehicles
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 rounded-lg text-base font-medium ${
                    isActive(item.path)
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {item.label}
                </button>
              )
            )}
            
            {/* Customer Account Buttons for Mobile */}
            <div className="pt-4 space-y-2 border-t border-gray-700">
              {isAuthenticated() ? (
                <>
                  <Button
                    onClick={() => {
                      navigate('/customer/dashboard');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full text-gray-300 hover:bg-gray-800"
                  >
                    {customer?.full_name || 'My Account'}
                  </Button>
                  <Button
                    onClick={() => {
                      logout();
                      navigate('/');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      navigate('/customer/login');
                      setMobileMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full text-gray-300 hover:bg-gray-800"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/customer/register');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;